/**
 * ProgressBar Component
 * 
 * Fixed progress indicator at the top of the viewport.
 * Shows:
 * - Visual progress bar with animation
 * - Step counter text
 * 
 * Automatically updates based on funnel state.
 */

import React from 'react';
import { motion } from 'framer-motion';

// ============================================
// TYPES
// ============================================

interface ProgressBarProps {
  /** Current step number (1-indexed) */
  currentStep: number;
  /** Total number of steps */
  totalSteps: number;
  /** Progress percentage (0-100) */
  progress: number;
  /** Hide the step counter text */
  hideStepCounter?: boolean;
}

// ============================================
// COMPONENT
// ============================================

/**
 * ProgressBar - Visual indicator of funnel progress
 */
export const ProgressBar: React.FC<ProgressBarProps> = ({
  currentStep,
  totalSteps,
  progress,
  hideStepCounter = false,
}) => {
  // Clamp progress between 0 and 100
  const clampedProgress = Math.min(100, Math.max(0, progress));

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      {/* Progress bar container */}
      <div className="h-1 bg-gray-200">
        {/* Animated progress fill */}
        <motion.div
          className="h-full bg-gradient-to-r from-primary to-primary-400"
          initial={{ width: 0 }}
          animate={{ width: `${clampedProgress}%` }}
          transition={{
            duration: 0.5,
            ease: [0.25, 0.46, 0.45, 0.94], // Ease out quart
          }}
          role="progressbar"
          aria-valuenow={clampedProgress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Progress: ${clampedProgress}%`}
        />
      </div>

      {/* Step counter */}
      {!hideStepCounter && (
        <div className="bg-white/80 backdrop-blur-sm border-b border-gray-100">
          <div className="max-w-question mx-auto px-6 py-2">
            <motion.p
              key={currentStep}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-sm text-text-secondary font-medium"
            >
              Step{' '}
              <span className="text-primary font-bold">{currentStep}</span>
              {' '}of{' '}
              <span className="font-bold">{totalSteps}</span>
            </motion.p>
          </div>
        </div>
      )}
    </div>
  );
};

ProgressBar.displayName = 'ProgressBar';

// ============================================
// MINI PROGRESS INDICATOR
// ============================================

interface MiniProgressProps {
  /** Progress percentage (0-100) */
  progress: number;
  /** Size of the indicator */
  size?: 'sm' | 'md' | 'lg';
  /** Show percentage text */
  showText?: boolean;
}

/**
 * MiniProgress - Circular progress indicator
 * Can be used in headers or cards
 */
export const MiniProgress: React.FC<MiniProgressProps> = ({
  progress,
  size = 'md',
  showText = false,
}) => {
  // Size configurations
  const sizes = {
    sm: { width: 24, stroke: 3 },
    md: { width: 32, stroke: 4 },
    lg: { width: 48, stroke: 5 },
  };

  const { width, stroke } = sizes[size];
  const radius = (width - stroke) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={width} height={width} className="-rotate-90">
        {/* Background circle */}
        <circle
          cx={width / 2}
          cy={width / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          className="text-gray-200"
        />
        {/* Progress circle */}
        <motion.circle
          cx={width / 2}
          cy={width / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="round"
          className="text-primary"
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          style={{ strokeDasharray: circumference }}
        />
      </svg>
      
      {/* Percentage text */}
      {showText && (
        <span className="absolute text-xs font-bold text-text-primary">
          {Math.round(progress)}%
        </span>
      )}
    </div>
  );
};

MiniProgress.displayName = 'MiniProgress';

export default ProgressBar;
